// Copyright ©️ 2026 eVolve MEP, LLC

import { type Shipment, useEvolvePaginatedGet } from '@evolvemep/api-ts';
import { Button } from '@mantine/core';

import { OptionalDetailSection } from 'components/Mantine/DetailPage/OptionalDetailSection';
import { useIsMobile } from 'hooks/useIsMobile';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import { ShipmentItemRow } from 'modules/Shop/Shipments/ShipmentDetail/ShipmentItemsList/ShipmentItemRow';
import { useCanEditShipment } from 'modules/Shop/Shipments/ShipmentDetail/useCanEditShipment';

import { useShipmentModals } from '../../modalRegistries';

type Props = {
  shipment: Shipment;
  onItemsUpdated: () => void;
};

export const ShipmentItemsList = ({ shipment, onItemsUpdated }: Props) => {
  const { openModal } = useShipmentModals();
  const { canEditShipment } = useCanEditShipment(shipment);
  const isMobile = useIsMobile();
  const canEdit = canEditShipment('addEditDeleteItems');

  const {
    data: items,
    fetchNextPage,
    loading,
    entireCount,
    reset: resetItems,
  } = useEvolvePaginatedGet('shop/shipment/:shipmentId/item', {
    defaultConfig: { url: `shop/shipment/${shipment.shipmentId}/item` },
  });

  const handleItemsChanged = () => {
    resetItems();
    onItemsUpdated();
  };

  return (
    <OptionalDetailSection
      title="Shipping Items"
      large
      noContentMessage="No items added to this shipping order."
      showNoContentMessage={!loading && entireCount === 0}
      enableDetailSection={!isMobile}
      rightSection={
        /** The "add items" menu is not yet ready for mobile */
        canEdit &&
        !isMobile && (
          <Button
            size="xs"
            variant="light"
            onClick={() => openModal('AddShipmentItemsModal', { shipment, onItemsAdded: handleItemsChanged })}
          >
            Add item
          </Button>
        )
      }
    >
      {items.map((item) => (
        <ShipmentItemRow
          key={item.shipmentItemId}
          item={item}
          canEdit={canEdit}
          onItemUpdated={handleItemsChanged}
          shipment={shipment}
        />
      ))}
      <TriggerOnVisible onVisible={fetchNextPage} loading={loading} />
    </OptionalDetailSection>
  );
};
